/**
 * Operator Form Component
 * Reusable form component for both Create and Edit operations
 */

import { useState, useEffect } from 'react'
import { validateForm } from '../../utils/validation'
import { showError } from '../../utils/toast'

export default function OperatorForm({ initialData = null, onSubmit, onCancel, isEdit = false }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    is_active: true,
  })
  const [errors, setErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)

  // Load initial data for edit mode (supports both flat and { operator } API shapes)
  useEffect(() => {
    if (!initialData) return

    const source = initialData.operator || initialData
    setFormData({
      name: source.name || '',
      email: source.email || '',
      password: '', // Don't pre-fill password
      is_active: source.is_active !== undefined ? Boolean(source.is_active) : true,
    })
    setErrors({})
  }, [initialData, isEdit])

  // Validation rules
  const validationRules = {
    name: {
      required: true,
      requiredMessage: 'Name is required',
      minLength: 2,
      minLengthMessage: 'Name must be at least 2 characters',
      maxLength: 100,
    },
    email: {
      required: true,
      requiredMessage: 'Email is required',
      email: true,
      emailMessage: 'Invalid email address',
    },
    password: {
      required: !isEdit, // Password required only for create
      requiredMessage: 'Password is required',
      minLength: 8,
      minLengthMessage: 'Password must be at least 8 characters',
      custom: (value) => {
        if (!isEdit && value && value.length < 8) {
          return 'Password must be at least 8 characters'
        }
        return null
      },
    },
  }

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }))
    
    // Clear error for this field when user starts typing
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = { ...prev }
        delete newErrors[name]
        return newErrors
      })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    
    // Validate form
    const validation = validateForm(formData, validationRules)
    if (!validation.isValid) {
      setErrors(validation.errors)
      showError('Please fix the form errors')
      return
    }

    setIsSubmitting(true)
    try {
      // Prepare data (exclude password if empty in edit mode)
      const submitData = { ...formData }
      if (isEdit && !submitData.password) {
        delete submitData.password
      }
      
      await onSubmit(submitData)
    } catch (error) {
      // Handle server-side validation errors
      if (error.errors && typeof error.errors === 'object' && Object.keys(error.errors).length > 0) {
        // Map server errors to form errors
        const serverErrors = {}
        Object.keys(error.errors).forEach(field => {
          const fieldErrors = error.errors[field]
          if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
            serverErrors[field] = fieldErrors[0] // Use first error message
          } else if (typeof fieldErrors === 'string') {
            serverErrors[field] = fieldErrors
          }
        })
        
        if (Object.keys(serverErrors).length > 0) {
          setErrors(serverErrors)
          // Show the first field error as a toast for visibility
          const firstError = Object.values(serverErrors)[0]
          if (firstError) {
            showError(firstError)
          }
          return
        }
      }
      
      // For other errors, show the error message
      if (error.message) {
        showError(error.message)
      }
      console.error('Form submission error:', error)
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Name Field */}
      <div>
        <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
          Name <span className="text-red-500">*</span>
        </label>
        <input
          type="text"
          id="name"
          name="name"
          value={formData.name}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
            errors.name ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter operator name"
        />
        {errors.name && (
          <p className="mt-1 text-sm text-red-600">{errors.name}</p>
        )}
      </div>

      {/* Email Field */}
      <div>
        <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
          Email <span className="text-red-500">*</span>
        </label>
        <input
          type="email"
          id="email"
          name="email"
          value={formData.email}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
            errors.email ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder="Enter email address"
        />
        {errors.email && (
          <p className="mt-1 text-sm text-red-600">{errors.email}</p>
        )}
      </div>

      {/* Password Field */}
      <div>
        <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
          Password {!isEdit && <span className="text-red-500">*</span>}
          {isEdit && <span className="text-gray-400 text-xs">(Leave blank to keep current)</span>}
        </label>
        <input
          type="password"
          id="password"
          name="password"
          value={formData.password}
          onChange={handleChange}
          className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
            errors.password ? 'border-red-500' : 'border-gray-300'
          }`}
          placeholder={isEdit ? "Enter new password (optional)" : "Enter password"}
        />
        {errors.password && (
          <p className="mt-1 text-sm text-red-600">{errors.password}</p>
        )}
        {!isEdit && (
          <p className="mt-1 text-xs text-gray-500">Must be at least 8 characters</p>
        )}
      </div>

      {/* Active Status Field */}
      <div className="flex items-center">
        <input
          type="checkbox"
          id="is_active"
          name="is_active"
          checked={formData.is_active}
          onChange={handleChange}
          className="h-4 w-4 text-indigo-600 focus:ring-indigo-500 border-gray-300 rounded"
        />
        <label htmlFor="is_active" className="ml-2 block text-sm text-gray-700">
          Active
        </label>
      </div>

      {/* Form Actions */}
      <div className="flex gap-3 pt-4">
        <button
          type="submit"
          disabled={isSubmitting}
          className="flex-1 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
        >
          {isSubmitting ? 'Saving...' : isEdit ? 'Update Operator' : 'Create Operator'}
        </button>
        {onCancel && (
          <button
            type="button"
            onClick={onCancel}
            disabled={isSubmitting}
            className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed font-medium"
          >
            Cancel
          </button>
        )}
      </div>
    </form>
  )
}
